import { isValidPhoneNumber, validatePhoneNumberLength } from 'libphonenumber-js/max';
import translations from './translations';

// Utils
import { parseCountryCodesInPartialNumber } from './helper';

// Types
import type { Language } from '$lib/types/Language';
import { InputValidationType } from '$lib/types/Form';

export const enum ValidationError {
    Required = 'Required',
    EmptyText = 'EmptyText',
    InvalidNumber = 'InvalidNumber',
    PhoneTooLong = 'PhoneTooLong',
    PhoneTooShort = 'PhoneTooShort',
    InvalidPhone = 'InvalidPhone',
    InvalidEmail = 'InvalidEmail',
    InvalidUrl = 'InvalidUrl',
    InvalidBirthday = 'InvalidBirthday',
}

export const getTranslatedValidationError = (error: ValidationError, language: Language) => {
    return translations[language].validation[error] ?? error;
};

export const validationMap = {
    [InputValidationType.REQUIRED]: (value: string, type: InputValidationType) => {
        if (!value) {
            return ValidationError.Required;
        }

        // For phoneV2 types only
        // If value is only the phone code and the input is required, consider the field empty
        if (
            type === InputValidationType.PHONE_V2 &&
            parseCountryCodesInPartialNumber(value)?.isPhoneCodeOnly
        ) {
            return ValidationError.Required;
        }
    },
    [InputValidationType.TEXT]: (value: string) => {
        if (value && !value.length) {
            return ValidationError.EmptyText;
        }
    },
    [InputValidationType.NUMBER]: (value: string) => {
        if (value && !value.match(/^[0-9]*$/)) {
            return ValidationError.InvalidNumber;
        }
    },
    [InputValidationType.PHONE]: (value: string) => {
        if (value && !value.match(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/)) {
            return ValidationError.InvalidPhone;
        }
    },
    [InputValidationType.PHONE_V2]: (value: string) => {
        if (!value || parseCountryCodesInPartialNumber(value)?.isPhoneCodeOnly) {
            // Only validate if there is something
            // If the field is required it will be validated by .REQUIRED
            // Additionally, if the value is only the phone code, this will be handled by .REQUIRED
            // This is needed here to not trigger the phone code validation if the field
            // is prefilled with the phone code of a country.
            return;
        }

        const lengthError = validatePhoneNumberLength(value);

        if (lengthError === 'TOO_LONG') {
            return ValidationError.PhoneTooLong;
        }

        if (lengthError === 'TOO_SHORT') {
            return ValidationError.PhoneTooShort;
        }

        if (!isValidPhoneNumber(value)) {
            return ValidationError.InvalidPhone;
        }
    },
    [InputValidationType.EMAIL]: (value: string) => {
        if (
            value &&
            !value.match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            )
        ) {
            return ValidationError.InvalidEmail;
        }
    },
    [InputValidationType.URL]: (value: string) => {
        if (
            value &&
            !value.match(/^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/)
        ) {
            return ValidationError.InvalidUrl;
        }
    },
    [InputValidationType.TEXTAREA]: (value: string) => {
        if (value && !value.length) {
            return ValidationError.EmptyText;
        }
    },
    [InputValidationType.BIRTHDAY]: (value: string) => {
        if (value === 'invalid') {
            return ValidationError.InvalidBirthday;
        }
    },
};

export const composeValidation =
    (...validators: ((value: string) => ValidationError | undefined)[]) =>
    (value: string) => {
        return validators.reduce(
            (error: string | undefined, validator) => error || validator(value),
            undefined,
        );
    };
